import { RESERVED_DONOR_TAG_NAMES } from '../../core/constants';

/**
 * Resolve the three reserved donor buckets against the tenant's tags and the
 * fetched contact counts.
 *
 * Tags are matched BY NAME because that is the only stable handle the frontend
 * has — ids are per-tenant. The names must stay in step with RESERVED_DONOR_TAGS
 * in backend/onboarding-api/lib/constants/index.ts.
 *
 * A group whose tag does not exist yet (a tenant provisioned before the reserved
 * tags were seeded, or one the backfill has not reached) resolves with `tag: null`
 * and is rendered as unavailable rather than as empty — "you have not imported
 * these yet" would be a lie when the bucket itself is missing.
 */
const normalize = (value) => value.trim().toLowerCase();

export const resolveDonorGroups = (tags, contactCounts = {}) => {
  const byName = new Map(tags.map((tag) => [normalize(tag.name), tag]));

  return RESERVED_DONOR_TAG_NAMES.map((name) => {
    const tag = byName.get(normalize(name)) ?? null;

    // A KEY-PRESENCE check, not `?? null`: an absent key means the count has not
    // been fetched yet, whereas a present `null` means the request ran and failed.
    // Collapsing the two made the cards flash "count unavailable" during the normal
    // loading window, which reads as an error rather than as progress.
    const hasCount = !!tag && Object.prototype.hasOwnProperty.call(contactCounts, tag.id);
    const count = hasCount ? contactCounts[tag.id] : null;

    return {
      name,
      tag,
      count,
      // Awaiting a first count for a tag that exists. Distinct from `count === null`
      // with `countPending: false`, which is a genuine read failure.
      countPending: !!tag && !hasCount,
      // Only true when we have a real, positive count. An unknown count is not
      // treated as filled — better to under-claim than to tell someone a bucket is
      // done when we could not read it.
      filled: typeof count === 'number' && count > 0,
      available: !!tag,
    };
  });
};

/** True while any existing bucket is still awaiting its first count. */
export const hasPendingCounts = (groups) => groups.some((group) => group.countPending);

/** How many of the three buckets have contacts. */
export const countFilledGroups = (groups) => groups.filter((group) => group.filled).length;

/** Ids to fetch counts for — skips groups whose tag is missing. */
export const donorGroupTagIds = (groups) => groups.filter((g) => g.tag).map((g) => g.tag.id);
